import { matrixInversion } from "./matrixInversion";
import { reconstructCovAndZ } from "./reconstructCovAndZ";
import { normalCdf } from "./normalCdf";

export function thurstoneMosteller(twoAfc) {
    const matrix = twoAfc.matrix;
    const sortedMethods = twoAfc.sortedMethods;
    const N = sortedMethods.length;

    // start from BT betas if possible
    let beta = twoAfc.betas ? twoAfc.betas.slice() : new Array(N).fill(0);

    const maxIter = 200;
    const tol = 1e-6;

    function phi(x) {
        return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
    }

    function Phi(x) {
        return 0.5 * (1 + erf(x / Math.SQRT2));
    }

    // numerical approximation
    function erf(x) {
        const sign = x >= 0 ? 1 : -1;
        x = Math.abs(x);
        const a1 = 0.254829592,
            a2 = -0.284496736,
            a3 = 1.421413741,
            a4 = -1.453152027,
            a5 = 1.061405429,
            p = 0.3275911;
        const t = 1 / (1 + p * x);
        const y =
            1 -
            (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) *
                t *
                Math.exp(-x * x));
        return sign * y;
    }

    let hess = null;

    // Newton–Raphson optimization
    for (let iter = 0; iter < maxIter; iter++) {
        let grad = new Array(N).fill(0);
        hess = Array.from({ length: N }, () => new Array(N).fill(0));

        for (let i = 0; i < N; i++) {
            for (let j = 0; j < N; j++) {
                if (i === j) continue;

                const nij = matrix[i][j] + matrix[j][i];
                if (nij === 0) continue;

                const diff = beta[i] - beta[j];
                const pij = Phi(diff);
                const dij = matrix[i][j];
                const w = phi(diff);

                grad[i] += dij - nij * pij;
                grad[j] += (nij * pij) - dij;

                // Hessian contributions
                hess[i][i] -= nij * w;
                hess[j][j] -= nij * w;
                hess[i][j] += nij * w;
                hess[j][i] += nij * w;
            }
        }

        // single stable ridge
        for (let i = 0; i < N; i++) hess[i][i] -= 1e-4;

        // invert Hessian
        let inv = matrixInversion(hess);

        // fallback ridge
        if (!inv) {
            for (let i = 0; i < N; i++) hess[i][i] -= 1e-2;
            inv = matrixInversion(hess);
            if (!inv) break;
        }

        // Newton step
        const step = new Array(N).fill(0);
        for (let i = 0; i < N; i++) {
            for (let j = 0; j < N; j++) {
                step[i] += inv[i][j] * grad[j];
            }
        }

        // update betas
        let maxChange = 0;
        for (let i = 0; i < N; i++) {
            if (Math.abs(step[i]) < 1e-6) step[i] = Math.sign(step[i]) * 1e-6;
            beta[i] -= step[i];
            maxChange = Math.max(maxChange, Math.abs(step[i]));
        }

        // normalize betas
        const mean = beta.reduce((s, v) => s + v, 0) / N;
        for (let i = 0; i < N; i++) beta[i] -= mean;

        if (maxChange < tol) break;
    }

    // Fisher information matrix
    const I = Array.from({ length: N }, () => Array(N).fill(0));

    for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
            if (i === j) continue;

            const nij = matrix[i][j] + matrix[j][i];
            if (nij === 0) continue;

            const diff = beta[i] - beta[j];
            const pij = Phi(diff);
            const phiVal = phi(diff);

            const v = nij * (phiVal * phiVal) / (pij * (1 - pij));

            I[i][i] += v;
            I[i][j] -= v;
        }
    }

    const se = new Array(N).fill(NaN);
    const z = new Array(N).fill(NaN);
    for (let i = 0; i < N; i++) {
        se[i] = I[i][i] > 0 ? 1 / Math.sqrt(I[i][i]) : NaN;
        z[i] = !isNaN(se[i]) && se[i] > 0 ? beta[i] / se[i] : NaN;
    }

    const p = z.map(v =>
        isNaN(v) ? NaN : 2 * (1 - normalCdf(Math.abs(v)))
    );

    return { betas: beta, se, z, p, covFull: null };
}
